/**
 * Services des quarts d'heure sécurité.
 *
 * Noter est N2 et ouvert à tout rôle : la base vérifie que l'appelant a
 * affaire au chantier, que la date est plausible, et qu'un compte de terrain ne
 * signe pas pour un autre (`noter_quart_heure_securite`). Lire aussi : la base
 * borne le terrain aux chantiers où il est affecté. Retirer un quart d'heure
 * noté par erreur est réservé au bureau — une trace qu'on peut effacer depuis le
 * chantier n'en est pas une.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { CrewMember, SafetyTalk } from "./types.js";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ");

export const listSafetyTalksInput = z.object({
  confirmationId: z.string().optional(),
  projectIds: z.array(z.string().uuid()).optional(),
  /** Seulement ceux tenus à partir de cette date. */
  from: isoDate.optional(),
});

export const recordSafetyTalkInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid("Chantier attendu"),
  topic: z.string().trim().min(1, "Dites le thème du quart d'heure").max(120, "Thème trop long"),
  heldOn: isoDate.optional(),
  attendeeIds: z.array(z.string().uuid()).default([]),
  /** Ignoré pour un compte de terrain : c'est lui qui anime. */
  ledByEmployeeId: z.string().uuid().nullish(),
  notes: z
    .string()
    .trim()
    .max(2000, "Notes trop longues")
    .transform((v) => (v === "" ? null : v))
    .nullish(),
});

export const removeSafetyTalkInput = z.object({
  confirmationId: z.string().optional(),
  id: z.string().uuid("Identifiant de quart d'heure attendu"),
});

export const readSiteCrewInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid("Chantier attendu"),
  on: isoDate.optional(),
});

export type ListSafetyTalksInput = z.infer<typeof listSafetyTalksInput>;
export type RecordSafetyTalkInput = z.infer<typeof recordSafetyTalkInput>;
export type RecordSafetyTalkFormInput = z.input<typeof recordSafetyTalkInput>;
export type RemoveSafetyTalkInput = z.infer<typeof removeSafetyTalkInput>;
export type ReadSiteCrewInput = z.infer<typeof readSiteCrewInput>;

export interface SafetyRepository {
  list(
    ctx: ExecutionContext,
    filter: { projectIds?: string[]; from?: string },
  ): Promise<SafetyTalk[]>;
  record(ctx: ExecutionContext, command: Omit<RecordSafetyTalkInput, "confirmationId">): Promise<string>;
  remove(ctx: ExecutionContext, id: string): Promise<void>;
  crew(ctx: ExecutionContext, projectId: string, on?: string): Promise<CrewMember[]>;
}

export function createSafetyServices(repo: SafetyRepository) {
  const listerQuartsHeure = defineService<ListSafetyTalksInput, SafetyTalk[]>({
    name: "lister_quarts_heure_securite",
    level: ActionLevel.Read,
    handler: (ctx, input) =>
      repo.list(ctx, {
        ...(input.projectIds ? { projectIds: input.projectIds } : {}),
        ...(input.from ? { from: input.from } : {}),
      }),
  });

  const noterQuartHeure = defineService<RecordSafetyTalkInput, { id: string }>({
    name: "noter_quart_heure_securite",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => {
      const { confirmationId: _ignored, ...command } = input;
      return { id: await repo.record(ctx, command) };
    },
  });

  const supprimerQuartHeure = defineService<RemoveSafetyTalkInput, void>({
    name: "supprimer_quart_heure_securite",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => repo.remove(ctx, input.id),
  });

  const equipeDuChantier = defineService<ReadSiteCrewInput, CrewMember[]>({
    name: "equipe_du_chantier",
    level: ActionLevel.Read,
    handler: (ctx, input) => repo.crew(ctx, input.projectId, input.on),
  });

  return { listerQuartsHeure, noterQuartHeure, supprimerQuartHeure, equipeDuChantier };
}

export type SafetyServices = ReturnType<typeof createSafetyServices>;
